import React, { memo, useContext } from "react";
import Head from "next/head";
import QueryManagerContext from "context/QueryManager/QueryManagerContext";
import { useQuery, gql } from "@apollo/client";
import { sanitizeCMS, getFaqMetaData } from "util/seo";
import { areaQueryWithCmsContent_area_Market_cmsContent_faq } from "types/pages/office-space/m/areaQueryWithCmsContent";
import styles from "./style.module.scss";

type Props = {
  className?: string;
  sanitizedContent: string;
  sanitizedTitle: string | null;
  externalId?: string;
  faq: areaQueryWithCmsContent_area_Market_cmsContent_faq[] | null;
};
// Tests don't function with a import cycle
const AREA_QUERY_WITH_CMS_CONTENT = gql`
  query areaQueryWithCmsContent($externalId: ID!) {
    area(externalId: $externalId) {
      id
      externalId
      parameterizedName
      name
      ... on Neighborhood {
        visibility
        cmsContent {
          id: title
          title
          content
          faq {
            question
            answer
          }
        }
        __typename
      }
      ... on Market {
        name
        cmsContent {
          id: title
          title
          content
          faq {
            question
            answer
          }
        }
        __typename
      }
      ... on Submarket {
        visibility
        cmsContent {
          id: title
          title
          content
          faq {
            question
            answer
          }
        }
        __typename
      }
    }
  }
`;

/**
 * Component for rendering CMSContent
 * Sanitizing CMSContent server side is a performance choice - content is only sanitized once and is more efficient for the client side code
 */
function CMSContentWrapper({
  className = "",
  sanitizedTitle,
  sanitizedContent,
  externalId,
  faq,
}: Props) {
  const { values }: any = useContext(QueryManagerContext);
  const { area } = values;
  const updated = !!externalId && externalId !== area;
  const { data, loading } = useQuery(AREA_QUERY_WITH_CMS_CONTENT, {
    variables: {
      externalId: area,
    },
    skip: !updated,
  });
  let updatedSanitizedCMS;
  if (data && !loading) {
    const areaData = data?.area;
    const { __typename: areaType } = areaData;
    updatedSanitizedCMS =
      areaType !== "City" && areaData.cmsContent
        ? sanitizeCMS(areaData.cmsContent)
        : null;
  }

  const currentContent = updatedSanitizedCMS?.content || sanitizedContent;
  const currentTitle = updatedSanitizedCMS?.title || sanitizedTitle;
  const currentFAQ = updatedSanitizedCMS?.faq || faq;

  return (
    <>
      <Head>
        {currentFAQ?.length && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getFaqMetaData(currentFAQ)),
            }}
          />
        )}
      </Head>
      <div
        data-qa="cmsContentWrapper"
        className={`${styles.container} ${className}`}
      >
        {currentTitle && <h1>{currentTitle}</h1>}
        <article
          // Data must be sanitized in parent component
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: currentContent }}
        />
      </div>
    </>
  );
}

export default memo(CMSContentWrapper);
